import React, { useState, useContext, useEffect} from 'react';
import {
    Button, TextField, Box, Typography,
    Container, Card, CardContent,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Alert, CircularProgress, useMediaQuery,
    DialogContentText, Divider, Chip
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import logo from '../img/injupemplogogobierno.png';
import { ContextSession } from '../../services/ContextSession';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import MostrarID from './images/MostrarID.jpg'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PersonIcon from '@mui/icons-material/Person';

const defaultTheme = createTheme();

export default function ActualizarCorreo() {
    const { sessionData } = useContext(ContextSession);
    const navigate = useNavigate();
  
    useEffect(() => {
      // Si el usuario no tiene permiso para acceder, redirigir a la página de inicio
      if (!sessionData.canAccessUpdateEmail) {
        navigate('/'); 
      }
    }, [sessionData, navigate]);

    const isMobile = useMediaQuery('(max-width:450px)');
    const sessi = useContext(ContextSession);

    // Estados
    const [nombre, setNombre] = useState('');
    const [identidad, setIdentidad] = useState('');
    const [nuevoCorreo, setNuevoCorreo] = useState('');
    const [nuevoCorreoConfirmar, setNuevoCorreoConfirmar] = useState('');
    const [telefono, setTelefono] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openDialog2, setOpenDialog2] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [apiMessage, setApiMessage] = useState('');

    // Estados para las imágenes
    const [frontID, setFrontID] = useState(null);
    const [backID, setBackID] = useState(null);
    const [photoWithID, setPhotoWithID] = useState(null);

    // Estado para almacenar los nombres de los archivos
    const [fileNames, setFileNames] = useState({
        front: '',
        back: '',
        photo: ''
    });

    // Estado para las vistas previas de las imágenes
    const [previewFront, setPreviewFront] = useState(null);
    const [previewBack, setPreviewBack] = useState(null);
    const [previewPhoto, setPreviewPhoto] = useState(null);

    // Expresión regular para validar correo electrónico
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const [fieldErrors, setFieldErrors] = useState({
        nombre: '',
        identidad: '',
        nuevoCorreo: '',
        nuevoCorreoConfirmar: '',
        telefono: '',
        frontID: '',
        backID: '',
        photoWithID: ''
    });


    const handleValidacionesCampos = () => {
        let valid = true;
        const newFieldErrors = {};
    
        if (!nombre) {
            newFieldErrors.nombre = 'El nombre es obligatorio.';
            valid = false;
        }
    
        if (!identidad || identidad.length !== 13 || isNaN(identidad)) {
            newFieldErrors.identidad = 'La identidad debe tener 13 dígitos.';
            valid = false;
        }
    
        if (!nuevoCorreo || !emailRegex.test(nuevoCorreo)) {
            newFieldErrors.nuevoCorreo = 'Por favor ingrese un correo electrónico válido.';
            valid = false;
        }
    
        if (nuevoCorreo !== nuevoCorreoConfirmar) {
            newFieldErrors.nuevoCorreoConfirmar = 'Los correos electrónicos no coinciden.';
            valid = false;
        }
    
        if (!telefono || telefono.length !== 8 || isNaN(telefono)) {
            newFieldErrors.telefono = 'El número de teléfono debe tener 8 dígitos.';
            valid = false;
        }
    
        if (!frontID) {
            newFieldErrors.frontID = 'Debe subir la imagen del frente de la tarjeta de identidad.';
            valid = false;
        }
    
        if (!backID) {
            newFieldErrors.backID = 'Debe subir la imagen del reverso de la tarjeta de identidad.';
            valid = false;
        }
    
        if (!photoWithID) {
            newFieldErrors.photoWithID = 'Debe subir una fotografía con la tarjeta de identidad.';
            valid = false;
        }
    
        setFieldErrors(newFieldErrors);  // Actualiza los errores de campo
    
        // Si hay errores, establece el mensaje de error y retorna false
        if (!valid) {
            validarFormulario();
        } else {
            setError(''); // Limpia el mensaje de error si no hay errores
        }
    
        return valid;  // Retorna si todo es válido o no
    };

    // Validaciones del formulario
    const validarFormulario = () => {
        if (!nombre || !identidad || !nuevoCorreo || !telefono) {
            setError('Todos los campos son obligatorios.');
            return false;
        }

        if (nuevoCorreo !== nuevoCorreoConfirmar) {
            setError('El nuevo correo electrónico debe coincidir con el ingresado en confirmar nuevo correo electrónico.');
            return false;
        }

        if (telefono.length !== 8 || isNaN(telefono)) {
            setError('El número de teléfono debe tener 8 dígitos.');
            return false;
        }

        if (identidad.length !== 13 || isNaN(identidad)) {
            setError('La identidad debe tener 13 dígitos.');
            return false;
        }

        if (!emailRegex.test(nuevoCorreo)) {
            setError('Por favor ingrese un correo electrónico válido.');
            return false;
        }

        if (!frontID) {
            setError('Debe subir la imagen del frente de la tarjeta de identidad.');
            return false;
        }
        if (!backID) {
            setError('Debe subir la imagen del reverso de la tarjeta de identidad.');
            return false;
        }
        if (!photoWithID) {
            setError('Debe subir la fotografía con la tarjeta de identidad.');
            return false;
        }

        setError('');
        return true;
    };

    // Función para manejar la entrada del Número de Identidad (solo números)
    const handleIdentidadChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value) && value.length <= 13) {
            setIdentidad(value);
        }
    };

    // Función para manejar la entrada del Número de Teléfono (solo números)
    const handleTelefonoChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value) && value.length <= 8) {
            setTelefono(value);
        }
    };

    // Manejo de la subida de imágenes
    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            // Validación del tamaño del archivo (máximo 4MB)
            if (file.size > 4 * 1024 * 1024) {
                setError('El archivo no puede ser mayor a 4MB.');
                return;
            }

            const validFormats = ['image/png', 'image/jpg', 'image/jpeg'];
            if (!validFormats.includes(file.type)) {
                setError('Solo se permiten archivos PNG, JPG o JPEG.');
                return;
            }
            setError('');

            // Vista previa de la imagen
            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'front') {
                    setPreviewFront(reader.result);
                    setFrontID(file);
                    setFileNames((prevState) => ({ ...prevState, front: file.name }));
                }
                if (type === 'back') {
                    setPreviewBack(reader.result);
                    setBackID(file);
                    setFileNames((prevState) => ({ ...prevState, back: file.name }));
                }
                if (type === 'photo') {
                    setPreviewPhoto(reader.result);
                    setPhotoWithID(file);
                    setFileNames((prevState) => ({ ...prevState, photo: file.name }));
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEnviar = async (event) => {
        event.preventDefault();

        if (handleValidacionesCampos()) {
            setLoading(true);

            const formData = new FormData();
            formData.append('dni', identidad);
            formData.append('nombre', nombre);
            formData.append('correoNuevo', nuevoCorreo);
            formData.append('telefono', telefono);
            formData.append('foto_1', frontID);
            formData.append('foto_2', backID);
            formData.append('foto_3', photoWithID);

            try {
                const response = await fetch('https://be.injupemp.gob.hn:8443/api/cambioCorreo/', {
                    method: 'POST',
                    body: formData
                });

                const data = await response.json();

                if (response.ok) {
                    setApiMessage('La solicitud de actualización de correo electrónico se envió con éxito. Recibirá una llamada al número de teléfono proporcionado para verificar sus datos y afiliación al INJUPEMP.');
                    setOpenDialog(true);
                } else {
                    setApiMessage(data.message || 'Ocurrió un error al enviar la solicitud.');
                    setOpenDialog(true);
                }
            } catch (error) {
                setApiMessage('Error al intentar conectar con la API. Intente más tarde.');
                setOpenDialog(true);
            } finally {
                setLoading(false);
            }
        }
    };


    const handleCloseDialog = () => {
        setOpenDialog(false);
        setNombre('');
        setIdentidad('');
        setNuevoCorreo('');
        setNuevoCorreoConfirmar('');
        setTelefono('');
        setFrontID(null);
        setBackID(null);
        setPhotoWithID(null);
        navigate('/');
    };

    const handleCloseDialog2 = () => {
        setOpenDialog2(false);
    };

    // Función para abrir el diálogo de la imagen de ejemplo
    const handleOpenDialog = () => {
        setOpenDialog2(true);
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <div style={{ backgroundColor: '#f0f0f0', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Container component="main" maxWidth="md" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <Card sx={{ width: '100%', padding: 1, borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ textAlign: 'center' }}>
                                <img src={logo} alt="logo" style={{ width: '130px', height: 'auto', marginBottom: '20px' }} />
                                <Typography variant="h5"><b>Solicitud de Actualización de Correo Electrónico</b></Typography>
                            </Box>
                            <Divider style={{ marginTop: 20 }}>
                                <Chip label="Datos Personales" size="medium" icon={<PersonIcon />} />
                            </Divider>
                            <TextField
                                fullWidth
                                label="Nombre Completo"
                                variant="outlined"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                style={{ marginTop: 15 }}
                                error={!!fieldErrors.nombre}  // Establece si hay error
                                helperText={fieldErrors.nombre}  // Muestra el mensaje de error
                            />

                            <TextField
                                fullWidth
                                label="Número de Identidad"
                                variant="outlined"
                                value={identidad}
                                onChange={handleIdentidadChange}
                                required
                                inputProps={{ maxLength: 13 }}
                                type="tel"
                                inputMode="numeric"
                                style={{ marginTop: 15 }}
                                error={!!fieldErrors.identidad}
                                helperText={fieldErrors.identidad}
                            />

                            <TextField
                                fullWidth
                                label="Nuevo Correo Electrónico"
                                variant="outlined"
                                value={nuevoCorreo}
                                onChange={(e) => setNuevoCorreo(e.target.value)}
                                required
                                type="email"
                                style={{ marginTop: 15 }}
                                error={!!fieldErrors.nuevoCorreo}
                                helperText={fieldErrors.nuevoCorreo}
                            />

                            <TextField
                                fullWidth
                                label="Confirmar Nuevo Correo Electrónico"
                                variant="outlined"
                                value={nuevoCorreoConfirmar}
                                onChange={(e) => setNuevoCorreoConfirmar(e.target.value)}
                                required
                                type="email"
                                style={{ marginTop: 15 }}
                                error={!!fieldErrors.nuevoCorreoConfirmar}
                                helperText={fieldErrors.nuevoCorreoConfirmar}
                            />

                            <TextField
                                fullWidth
                                label="Número de Teléfono"
                                variant="outlined"
                                value={telefono}
                                onChange={handleTelefonoChange}
                                required
                                inputProps={{ maxLength: 8 }}
                                type="tel"
                                inputMode="numeric"
                                style={{ marginTop: 15 }}
                                error={!!fieldErrors.telefono}
                                helperText={fieldErrors.telefono}
                            />
                            <Divider style={{ marginTop: 20 }}>
                                <Chip
                                    label="Adjuntar Fotografías"
                                    size="medium"
                                    icon={<AddPhotoAlternateIcon />} // Añadir el icono aquí
                                />
                            </Divider>
                            <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                                <Button variant="contained" component="label" fullWidth
                                    disabled={loading}
                                    startIcon={<FileUploadIcon sx={{ color: 'white' }} />}
                                    style={{ backgroundColor: '#bababa' }}>
                                    Fotografía Tarjeta de identidad (frente)
                                    <input type="file" accept="image/png, image/jpeg, image/jpg" hidden onChange={(e) => handleFileChange(e, 'front')} />
                                </Button>
                                {fileNames.front && <p>{fileNames.front}</p>}
                                {previewFront && (
                                    <Box sx={{
                                        mt: 2,
                                        border: '2px solid black',
                                        borderRadius: '8px',
                                        padding: '5px',
                                        maxWidth: '200px',
                                        margin: '0 auto'
                                    }}>
                                        <img src={previewFront} alt="Vista previa front" style={{ width: '100%', borderRadius: '8px' }} />
                                    </Box>
                                )}

                                <Button component="label" fullWidth
                                    variant="contained"
                                    sx={{ mt: 3 }}
                                    disabled={loading}
                                    startIcon={<FileUploadIcon sx={{ color: 'white' }} />}
                                    style={{ backgroundColor: '#bababa' }}>
                                    Fotografía Tarjeta de identidad (reverso)
                                    <input type="file" accept="image/png, image/jpeg, image/jpg" hidden onChange={(e) => handleFileChange(e, 'back')} />
                                </Button>
                                {fileNames.back && <p>{fileNames.back}</p>}
                                {previewBack && (
                                    <Box sx={{
                                        mt: 2,
                                        border: '2px solid black',
                                        borderRadius: '8px',
                                        padding: '5px',
                                        maxWidth: '200px',
                                        margin: '0 auto'
                                    }}>
                                        <img src={previewBack} alt="Vista previa back" style={{ width: '100%', borderRadius: '8px' }} />
                                    </Box>
                                )}

                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{ mt: 3 }}
                                    fullWidth
                                    disabled={loading}
                                    startIcon={<FileUploadIcon sx={{ color: 'white' }} />}
                                    style={{ backgroundColor: '#bababa' }}
                                    onClick={handleOpenDialog}
                                >
                                    Fotografía con tarjeta de identidad
                                </Button>
                                {fileNames.photo && <p>{fileNames.photo}</p>}
                                {previewPhoto && (
                                    <Box sx={{
                                        mt: 2,
                                        border: '2px solid black',
                                        borderRadius: '8px',
                                        padding: '5px',
                                        maxWidth: '200px',
                                        margin: '0 auto'
                                    }}>
                                        <img src={previewPhoto} alt="Vista previa photo" style={{ width: '100%', borderRadius: '8px' }} />
                                    </Box>
                                )}

                                {/* Diálogo con imagen de ejemplo */}
                                <Dialog open={openDialog2} onClose={handleCloseDialog2}>
                                    <DialogTitle style={{ textAlign: 'center' }}>Posición de la fotografía</DialogTitle>
                                    <DialogContent>
                                        <div style={{ textAlign: 'center' }}>
                                            <img
                                                src={MostrarID}
                                                alt="Posición de la fotografía"
                                                style={{ width: '100%', maxWidth: '400px' }}
                                            />
                                            <p style={{ textAlign: 'justify' }}>Adjunte una fotografía junto a su tarjeta de identidad. Debe asegurarse que su rostro y la tarjeta de identidad sean visibles, tal como la ilustración anterior.</p>
                                        </div>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleCloseDialog2} color="error">
                                            Cerrar
                                        </Button>
                                        <Button
                                            onClick={() => document.getElementById('photo-input').click()}
                                            style={{ backgroundColor: '#6BC5D0', color: 'white' }}
                                            startIcon={<FileUploadIcon sx={{ color: 'white' }} />}
                                        >
                                            Seleccionar fotografía
                                        </Button>
                                    </DialogActions>
                                </Dialog>

                                {/* Input de tipo archivo oculto */}
                                <input
                                    id="photo-input"
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg"
                                    hidden
                                    onChange={(e) => {
                                        handleFileChange(e, 'photo'); // Llama a handleFileChange
                                        handleCloseDialog2(); // Llama a handleCloseDialog2
                                    }}
                                />
                            </Box>
                            <Divider style={{ marginTop: 20 }}></Divider>
                            {error && <Alert severity="warning" sx={{ mt: 2, textAlign: 'justify' }}>{error}</Alert>}
                            <Button
                                onClick={handleEnviar}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={loading}
                                style={{ backgroundColor: '#6BC5D0' }}
                                endIcon={<SendIcon sx={{ color: 'white' }} />}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Enviar Solicitud'}
                            </Button>
                        </CardContent>
                    </Card>
                </Container>

                {/* Diálogo de confirmación */}
                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <CheckCircleIcon color={apiMessage.includes('éxito') ? 'success' : 'error'} sx={{ fontSize: 40, marginRight: 1 }} />
                            {apiMessage.includes('éxito') ? 'Solicitud enviada con éxito' : 'Error al enviar solicitud'}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography textAlign={"justify"}>{apiMessage}</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center' }}>
                        <Button onClick={handleCloseDialog} variant="contained" sx={{ backgroundColor: '#6BC5D0' }}>Aceptar</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </ThemeProvider>
    );
}


